import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { dynamicFields } from '../shared/constants.js';

const hasText = (s) => typeof s === 'string' && s.trim().length > 0;

function categorize(field, value) {
    if (!field || value === undefined || value === null) return '__NONE__';

    const num = Number(value);
    if (isNaN(num)) return '__NONE__';

    switch (field) {
        case 'lengthKm':
            if (num < 5)  return '< 5 km';
            if (num < 10) return '5-10 km';
            if (num < 20) return '10-20 km';
            return '> 20 km';

        case 'ascentMeters':
            if (num < 300)  return '< 300 m';
            if (num < 600)  return '300-600 m';
            if (num < 1000) return '600-1000 m';
            return '> 1000 m';

        case 'totalDurationMin':
            if (num < 120) return '< 2h';
            if (num < 240) return '2-4h';
            if (num < 360) return '4-6h';
            return '> 6h';

        case 'elevationMin':
        case 'elevationMax':
            if (num < 500)  return '< 500 m';
            if (num < 1000) return '500-1000 m';
            return '> 1000 m';

        case 'widthMeters':
            if (num < 1) return '< 1 m';
            if (num < 2) return '1-2 m';
            if (num < 3) return '2-3 m';
            return '> 3 m';

        default:
            return String(value);
    }
}

function computeScenery(feature) {
    const p = feature.properties || {};
    const reltags = Array.isArray(p['@relations']) && p['@relations'][0]?.reltags
        ? p['@relations'][0].reltags
        : {};

    const getAnyTag = (key) => p[key] ?? reltags[key];

    const name        = (getAnyTag('name')           || '').toLowerCase();
    const nameEn      = (getAnyTag('name:en')        || '').toLowerCase();
    const nameFr      = (getAnyTag('name:fr')        || '').toLowerCase();
    const nameDe      = (getAnyTag('name:de')        || '').toLowerCase();
    const nameEs      = (getAnyTag('name:es')        || '').toLowerCase();
    const description = (getAnyTag('description')    || '').toLowerCase();
    const descriptionEn = (getAnyTag('description:en') || '').toLowerCase();
    const descriptionFr = (getAnyTag('description:fr') || '').toLowerCase();
    const descriptionDe = (getAnyTag('description:de') || '').toLowerCase();
    const descriptionEs = (getAnyTag('description:es') || '').toLowerCase();
    const note        = (getAnyTag('note')           || '').toLowerCase();

    const fullText = [
        name, nameEn, nameFr, nameDe, nameEs,
        description, descriptionEn, descriptionFr, descriptionDe, descriptionEs,
        note
    ].join(' ').toLowerCase();

    const landuse  = getAnyTag('landuse');
    const natural  = getAnyTag('natural');
    const waterway = getAnyTag('waterway');
    const water    = getAnyTag('water');
    const scenic   = getAnyTag('scenic');

    // I valori 'Sì'/'No' sono chiavi interne dei filtri — vengono
    // tradotti solo al momento della visualizzazione tramite getDifficultyLabel
    return {
        scenic: scenic === 'yes' ||
            /panoram|belvedere|vista|affaccio|viewpoint|scenic|overlook|belvédère|vue|point.de.vue|aussicht|aussichtspunkt|mirador|panorámic/i.test(fullText)
            ? 'Sì' : 'No',
        forest: natural === 'wood' || landuse === 'forest' ||
            /bosc|forest|alberi|wood|selva|faggio|quercia|pino|abete|grove|woodland|forêt|bois|arbre|bosquet|wald|baum|forst|bäume|bosque|árbol|arboleda/i.test(fullText)
            ? 'Sì' : 'No',
        river: waterway === 'river' || waterway === 'stream' ||
            /fiume|torrent|ruscello|rio|vallone|river|stream|creek|brook|rivière|fleuve|ruisseau|fluss|bach|strom|arroyo/i.test(fullText)
            ? 'Sì' : 'No',
        lake: (natural === 'water' && water === 'lake') ||
            /lago|laghetto|bacino|lake|pond|reservoir|lac|étang|see|teich|laguna|embalse/i.test(fullText)
            ? 'Sì' : 'No',
        waterfall: waterway === 'waterfall' ||
            /cascata|salto|caduta.*acqua|waterfall|falls|cascade|chute.*eau|wasserfall|catarata/i.test(fullText)
            ? 'Sì' : 'No',
        coastal: natural === 'coastline' || natural === 'beach' ||
            /costa|mare|spiaggia|lido|scogliera|litorale|coast|sea|beach|shore|cliff|ocean|seaside|côte|mer|plage|falaise|océan|littoral|küste|meer|strand|klippe|ozean|acantilado/i.test(fullText)
            ? 'Sì' : 'No',
    };
}

function computeActivityTypes(feature) {
    const p = feature.properties || {};
    const reltags = Array.isArray(p['@relations']) && p['@relations'][0]?.reltags
        ? p['@relations'][0].reltags
        : {};

    const getAnyTag = (key) => p[key] ?? reltags[key];

    const bicycle  = getAnyTag('bicycle');
    const mtb      = getAnyTag('mtb');
    const mtbScale = getAnyTag('mtb:scale');
    let mtbCompatible;
    if (bicycle === 'yes' || bicycle === 'designated' || mtb === 'yes' || mtbScale !== undefined)
        mtbCompatible = 'Si';

    const horse   = getAnyTag('horse');
    const highway = getAnyTag('highway');
    let horseCompatible;
    if (horse === 'yes' || horse === 'designated' || highway === 'bridleway')
        horseCompatible = 'Si';

    const viaFerrataScale = getAnyTag('via_ferrata:scale');
    const climbing        = getAnyTag('climbing');
    let isViaFerrata;
    if (viaFerrataScale !== undefined || highway === 'via_ferrata' || climbing === 'via_ferrata')
        isViaFerrata = 'Si';

    let viaFerrataGrade;
    if (viaFerrataScale) {
        const match = viaFerrataScale.match(/K([1-6])/i);
        viaFerrataGrade = match ? `K${match[1]}` : viaFerrataScale;
    }

    let mtbScaleGrade;
    if (mtbScale) {
        const match = mtbScale.match(/[S]?([0-6])/i);
        mtbScaleGrade = match ? `S${match[1]}` : mtbScale;
    }

    const wheelchair = getAnyTag('wheelchair');
    let wheelchairAccess;
    if (wheelchair) {
        // Valori interni — tradotti da getDifficultyLabel al momento della visualizzazione
        const wheelchairMap = {
            'yes':        'Accessibile',
            'limited':    'Accessibilità limitata',
            'no':         'Non accessibile',
            'designated': 'Progettato per sedie a rotelle'
        };
        wheelchairAccess = wheelchairMap[wheelchair] || wheelchair;
    }

    return {
        mtbCompatible,
        horseCompatible,
        viaFerrata: isViaFerrata,
        viaFerrataGrade,
        mtbScaleGrade,
        wheelchair: wheelchairAccess
    };
}

function extractAvailableFilters(features, fields) {
    const result = {};
    const fieldHasValue = {};

    features.forEach((f) => {
        const props = f.properties || {};

        Object.keys(fields).forEach((field) => {
            const val = props[field];
            if (val !== undefined && val !== null && val !== '__NONE__') {
                fieldHasValue[field] = true;
                if (!result[field]) result[field] = new Set();
                result[field].add(val);
            }
        });

        const catFields = [
            'lengthCategory', 'ascentCategory', 'elevationRangeCategory',
            'totalDurationCategory', 'widthCategory',
        ];
        catFields.forEach((catField) => {
            const val = props[catField];
            if (val !== undefined && val !== null && val !== '__NONE__') {
                fieldHasValue[catField] = true;
                if (!result[catField]) result[catField] = new Set();
                result[catField].add(val);
            }
        });

        ['scenic', 'forest', 'river', 'lake', 'waterfall', 'coastal',
         'mtbCompatible', 'horseCompatible', 'viaFerrata'].forEach((boolField) => {
            const val = props[boolField];
            if (val !== undefined && val !== null && val !== '__NONE__') {
                fieldHasValue[val] = true;
                if (!result[boolField]) result[boolField] = new Set();
                result[boolField].add(val);
            }
        });

        ['viaFerrataGrade', 'mtbScaleGrade'].forEach((gradeField) => {
            const val = props[gradeField];
            if (val !== undefined && val !== null && val !== '__NONE__') {
                if (!result[gradeField]) result[gradeField] = new Set();
                result[gradeField].add(val ?? '__NONE__');
            }
        });

        const val = props['wheelchair'];
        if (val !== undefined && val !== null && val !== '__NONE__') {
            if (!result['wheelchair']) result['wheelchair'] = new Set();
            result['wheelchair'].add(val ?? '__NONE__');
        }
    });

    Object.keys(fields).forEach((field) => {
        if (fieldHasValue[field]) {
            features.forEach((f) => {
                const val = f.properties?.[field];
                if (val === undefined || val === null || val === '__NONE__') {
                    if (!result[field]) result[field] = new Set();
                    result[field].add('__NONE__');
                }
            });
        }
    });

    Object.keys(result).forEach((field) => {
        const values = result[field];
        const sortedArray = Array.from(values).sort((a, b) => {
            if (a === 'Sì') return -1;
            if (b === 'Sì') return 1;
            if (a === 'No') return -1;
            if (b === 'No') return 1;
            if (a === '__NONE__') return 1;
            if (b === '__NONE__') return -1;

            if (field === 'wheelchair') {
                const order = {
                    'Progettato per sedie a rotelle': 0,
                    'Accessibile':                   1,
                    'Accessibilità limitata':         2,
                    'Non accessibile':                3,
                    '__NONE__':                       4
                };
                return (order[a] ?? 5) - (order[b] ?? 5);
            }

            if (field === 'viaFerrataGrade') {
                const aMatch = a.match(/K([1-6])/);
                const bMatch = b.match(/K([1-6])/);
                if (aMatch && bMatch) return parseInt(aMatch[1]) - parseInt(bMatch[1]);
            }

            if (field === 'mtbScaleGrade') {
                const aMatch = a.match(/S([0-6])/);
                const bMatch = b.match(/S([0-6])/);
                if (aMatch && bMatch) return parseInt(aMatch[1]) - parseInt(bMatch[1]);
            }

            return String(a).localeCompare(String(b), undefined, { numeric: true });
        });

        if (sortedArray.length === 1) {
            delete result[field];
        } else {
            result[field] = sortedArray;
        }
    });

    return result;
}

export function useRegionFilters(regionData) {
    const { t } = useTranslation();

    const mappedFeatures = useMemo(() => {
        if (!regionData) return null;
        return regionData.features
            .map((f) => {
                const p = f.properties || {};
                const reltags = Array.isArray(p['@relations']) && p['@relations'][0]?.reltags
                    ? p['@relations'][0].reltags
                    : {};
                const getTag = (key) => p[key] ?? reltags[key] ?? null;

                const name            = getTag('name') || null;
                const lengthKm        = parseFloat(getTag('distance')) || null;
                const ascentMeters    = parseFloat(getTag('ascent'))   || null;
                const descentMeters   = parseFloat(getTag('descent'))  || null;
                const durationForward = getTag('duration:forward')
                    ? parseFloat(getTag('duration:forward').replace(/[^\d.]/g, ''))
                    : null;
                const durationBackward = getTag('duration:backward')
                    ? parseFloat(getTag('duration:backward').replace(/[^\d.]/g, ''))
                    : null;
                const totalDurationMin    = durationForward || durationBackward || null;
                const from               = getTag('from')        || null;
                const to                 = getTag('to')          || null;
                const via                = getTag('via')         || null;
                const note               = getTag('note')        || null;
                const description        =
                    getTag('description:it') ||
                    getTag('description')    ||
                    getTag('description:de') ||
                    getTag('description:fr') ||
                    getTag('description:es') ||
                    getTag('description:en') ||
                    getTag('note:it')        ||
                    getTag('note')           ||
                    null;
                const caiScale   = getTag('cai_scale')  || null;
                const sacScale   = getTag('sac_scale')  || null;
                const mtbScale   = getTag('mtb:scale')  || null;
                const difficulty = sacScale || mtbScale || caiScale || null;
                const elevationMin      = parseFloat(getTag('ele:min')) || null;
                const elevationMax      = parseFloat(getTag('ele:max')) || null;
                const widthMeters       = parseFloat(getTag('width'))   || null;
                const surface           = getTag('surface') || null;
                const lengthCategory        = categorize('lengthKm',      lengthKm);
                const ascentCategory        = categorize('ascentMeters',  ascentMeters);
                const totalDurationCategory = categorize('totalDurationMin', totalDurationMin);
                const elevationRangeCategory = categorize('elevationMin', elevationMin);
                const widthCategory         = categorize('widthMeters',   widthMeters);
                const scenery    = computeScenery(f);
                const activities = computeActivityTypes(f);

                return {
                    ...f,
                    properties: {
                        ...p,
                        name, lengthKm, ascentMeters, descentMeters,
                        totalDurationMin, from, to, via, note, description,
                        difficulty, elevationMin, elevationMax, widthMeters, surface,
                        lengthCategory, ascentCategory, totalDurationCategory,
                        elevationRangeCategory, widthCategory,
                        ...scenery,
                        ...activities
                    }
                };
            })
            .filter((feature) => {
                if (feature.geometry?.type === 'Point') {
                    const desc = feature.properties.description;
                    return desc !== null && desc !== undefined && desc.trim() !== '';
                }
                return true;
            });
    }, [regionData]);

    const dynamicFilters = useMemo(() => {
        if (!mappedFeatures) return {};
        return extractAvailableFilters(mappedFeatures, dynamicFields);
    }, [mappedFeatures]);

    const initialSelectedDynamic = useMemo(() => {
        if (Object.keys(dynamicFilters).length === 0) return {};
        console.log('🎯 Inizializzazione filtri - TUTTI ATTIVI');
        const initialSelection = {};
        Object.entries(dynamicFilters).forEach(([field, values]) => {
            initialSelection[field] = new Set(values);
            console.log(`  ✅ ${field}: ${values.length} valori selezionati`);
        });
        return initialSelection;
    }, [dynamicFilters]);

    const [selectedDynamic, setSelectedDynamic] = useState(initialSelectedDynamic);

    useEffect(() => {
        if (Object.keys(initialSelectedDynamic).length > 0) {
            console.log('🔄 Aggiornamento filtri per cambio regione');
            setSelectedDynamic(initialSelectedDynamic);
        }
    }, [initialSelectedDynamic]);

    const filteredFeatures = useMemo(() => {
        if (!mappedFeatures) return [];
        return mappedFeatures.filter((feature) => {
            const p = feature.properties || {};
            for (const [field, selectedValues] of Object.entries(selectedDynamic)) {
                if (selectedValues.size === 0) continue;
                const featureValueStr = String(p[field] ?? '__NONE__');
                if (!selectedValues.has(featureValueStr)) return false;
            }
            return true;
        });
    }, [mappedFeatures, selectedDynamic]);

    const filtersHash = useMemo(() =>
        JSON.stringify(Array.from(Object.entries(selectedDynamic)).sort()),
    [selectedDynamic]);

    const toggleDynamicFilter = useCallback((field, valueStr) => {
        console.log('⚙️ toggleDynamicFilter chiamato:', field, valueStr);
        setSelectedDynamic((prev) => {
            const current = prev[field] || new Set();
            const newSet  = new Set(current);
            if (newSet.has(valueStr)) {
                newSet.delete(valueStr);
            } else {
                newSet.add(valueStr);
            }
            if (newSet.size === 0) {
                const { [field]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [field]: newSet };
        });
    }, []);

    const resetFilters = useCallback(() => {
        console.log('🔄 resetFilters chiamato');
        const initialSelection = {};
        Object.entries(dynamicFilters).forEach(([field, values]) => {
            initialSelection[field] = new Set(values);
        });
        setSelectedDynamic(initialSelection);
    }, [dynamicFilters]);

    const getDifficultyLabel = useCallback((value) => {
        const labels = {
            // SAC scale
            'hiking':                    t('sac_T1'),
            'mountain_hiking':           t('sac_T2'),
            'demanding_mountain_hiking': t('sac_T3'),
            'alpine_hiking':             t('sac_T4'),
            'demanding_alpine_hiking':   t('sac_T5'),
            'difficult_alpine_hiking':   t('sac_T6'),
            // CAI scale
            'T':     t('cai_T'),
            'E':     t('cai_E'),
            'EE':    t('cai_EE'),
            'EEA':   t('cai_EEA'),
            'EEA/F': t('cai_EEA_F'),
            'F':     t('cai_F'),
            // MTB
            'mtb:scale:0': t('mtb_S0'),
            'mtb:scale:1': t('mtb_S1'),
            'mtb:scale:2': t('mtb_S2'),
            'mtb:scale:3': t('mtb_S3'),
            // Via Ferrata
            'K1': t('ferrata_K1'),
            'K2': t('ferrata_K2'),
            'K3': t('ferrata_K3'),
            'K4': t('ferrata_K4'),
            'K5': t('ferrata_K5'),
            'K6': t('ferrata_K6'),
            // MTB grades
            'S0': t('mtb_S0'),
            'S1': t('mtb_S1'),
            'S2': t('mtb_S2'),
            'S3': t('mtb_S3'),
            'S4': t('mtb_S4'),
            'S5': t('mtb_S5'),
            'S6': t('mtb_S6'),
            // Wheelchair
            'Accessibile':                    t('wheelchair_yes'),
            'Accessibilità limitata':         t('wheelchair_limited'),
            'Non accessibile':                t('wheelchair_no'),
            'Progettato per sedie a rotelle': t('wheelchair_designated'),
            // Generic
            '__NONE__': t('filter_none_label'),
            'Horse Compatible': t('horse_compatible'),
            'Mtb Compatible':   t('mtb_compatible'),
            'ground':           t('surface_ground'),
            'Sì': `✓ ${t('yes')}`,
            'No': `✗ ${t('no')}`,
        };
        return labels[value] || value;
    }, [t]); // t nelle dipendenze: si aggiorna al cambio lingua

    return {
        filteredFeatures,
        mappedFeatures,
        dynamicFilters,
        selectedDynamic,
        filtersHash,
        toggleDynamicFilter,
        resetFilters,
        getDifficultyLabel
    };
}
